import {
  ListAuditResult,
  SettingRow,
  SettingsAuditResult,
} from "@/lib/shopConfigAudit";

export type ShopAudit = {
  shopConfig: SettingsAuditResult;
  cardColors: SettingsAuditResult;
  enabledFeatures: SettingsAuditResult;
  tags: ListAuditResult;
  automations: ListAuditResult;
  // Set when this shop's tags/automations reads failed; `tags`/`automations`
  // are then untrustworthy and are excluded from every roll-up.
  fetchError?: string;
};

export type StatusCounts = { missing: number; drift: number; match: number };
export type AuditFilter = "issues" | "missing" | "drift" | "all";
export type Verdict = "incomplete" | "customized" | "clean";
export type ShopSummary = StatusCounts & { verdict: Verdict };
export type FilteredList = {
  missing: string[];
  extra: string[];
  present: string[];
};

export function countStatuses(rows: SettingRow[]): StatusCounts {
  const counts: StatusCounts = { missing: 0, drift: 0, match: 0 };
  for (const row of rows) counts[row.status] += 1;
  return counts;
}

export function summarizeShop(audit: ShopAudit): ShopSummary {
  const settings = countStatuses([
    ...audit.shopConfig.rows,
    ...audit.cardColors.rows,
    ...audit.enabledFeatures.rows,
  ]);
  const lists = audit.fetchError ? [] : [audit.tags, audit.automations];
  const missing =
    settings.missing + lists.reduce((n, l) => n + l.missing.length, 0);
  const match =
    settings.match + lists.reduce((n, l) => n + l.present.length, 0);
  const verdict: Verdict =
    missing > 0 ? "incomplete" : settings.drift > 0 ? "customized" : "clean";
  return { missing, drift: settings.drift, match, verdict };
}

export function filterSettings(
  rows: SettingRow[],
  filter: AuditFilter
): SettingRow[] {
  if (filter === "all") return rows;
  if (filter === "issues") return rows.filter((r) => r.status !== "match");
  return rows.filter((r) => r.status === filter);
}

// Mirrors the mockup: "Drifted" shows shop-added items (the only non-default
// items a list can have), "Not set" shows missing defaults, and defaults the
// shop already has only appear under "All".
export function filterLists(
  list: ListAuditResult,
  filter: AuditFilter
): FilteredList {
  return {
    missing: filter === "drift" ? [] : list.missing,
    extra: filter === "missing" ? [] : list.extra,
    present: filter === "all" ? list.present : [],
  };
}

export function formatAuditValue(value: unknown): string | null {
  if (value === undefined) return null;
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value);
  }
  return String(value);
}
